/**
 * Notification Bell Component
 *
 * Bell icon button with unread count badge.
 * Features:
 * - Unread count badge (max "99+")
 * - Subtle pulse animation on new notifications
 * - Click to toggle notification dropdown
 * - Accessible with proper ARIA attributes
 */

import { useState, useCallback } from 'react';
import { Tooltip, IconButton } from '@mui/material';
import { useNotifications } from '../../contexts/NotificationContext';
import { NotificationDropdown } from './NotificationDropdown';
import { transitions } from '../../theme/tokens';
import './NotificationBell.css';

export function NotificationBell() {
  const { unreadCount } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Format badge text
  const badgeText = unreadCount > 99 ? '99+' : unreadCount.toString();

  return (
    <div className="notification-bell">
      <Tooltip title={unreadCount > 0 ? `${unreadCount} notifications` : 'No new notifications'}>
        <IconButton
          onClick={handleToggle}
          size="small"
          aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
          aria-expanded={isOpen}
          aria-haspopup="true"
          sx={{
            color: 'text.secondary',
            transition: `background-color ${transitions.duration.fast}ms ${transitions.easing.easeInOut}`,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {/* Bell Icon */}
          <svg
            className="notification-bell__icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
          </svg>

          {/* Badge */}
          {unreadCount > 0 && (
            <span
              className={`notification-bell__badge ${unreadCount > 9 ? 'notification-bell__badge--wide' : ''}`}
            >
              {badgeText}
            </span>
          )}
        </IconButton>
      </Tooltip>

      {/* Dropdown */}
      <NotificationDropdown isOpen={isOpen} onClose={handleClose} />
    </div>
  );
}
